import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import type { FilaCuentaDia, PublicacionRendimiento } from '@/lib/instagram-metricas'
import type { Periodo } from '@/lib/periodo-reporte'

export interface InstagramCuenta {
  id: string
  ig_user_id: string
  username: string
  activa: boolean
  token_vence_at: string | null
  ultima_captura_at: string | null
  ultima_captura_estado: 'ok' | 'parcial' | 'error' | null
  ultima_captura_detalle: string | null
}

/** Clics del bloque de Instagram de un tipo de mail (Fase 3). `mails` son los mails distintos con al menos un clic. */
export interface ClicsPorTipo {
  tipoEmail: string
  clics: number
  mails: number
}

/** Publicación candidata al bloque de Instagram de los mails (plan-promo-instagram-mails, Fase 2). */
export interface PublicacionParaMail {
  id: string
  permalink: string | null
  caption: string | null
  media_type: string
  es_reel: boolean
  imagen_url: string
  publicado_at: string
  ocultar_en_mails: boolean
  tema: string | null
  /** Máximo de vistas de los últimos 30 días. */
  vistas: number | null
}

const COLUMNAS =
  'id, ig_user_id, username, activa, token_vence_at, ultima_captura_at, ultima_captura_estado, ultima_captura_detalle'

/** Tope de filas de PostgREST. Una respuesta de ese tamaño puede venir cortada. */
const LIMITE_FILAS = 1000

export interface RendimientoPublicaciones {
  publicaciones: PublicacionRendimiento[]
  recortada: boolean
}

type FilaRendimiento = Database['public']['Functions']['reporte_instagram_publicaciones']['Returns'][number]

const aNumero = (valor: unknown): number | null => (valor === null || valor === undefined ? null : Number(valor))

/** Postgres puede devolver numeric como string, así que las columnas numeric se pasan a número. */
const aPublicacion = (fila: FilaRendimiento): PublicacionRendimiento => ({
  ...fila,
  ult_avg_watch_time: aNumero(fila.ult_avg_watch_time),
  ult_skip_rate: aNumero(fila.ult_skip_rate),
  d7_avg_watch_time: aNumero(fila.d7_avg_watch_time),
  d7_skip_rate: aNumero(fila.d7_skip_rate),
})

const llamarFunction = async <T>(token: string, body: Record<string, unknown>): Promise<T> => {
  const res = await fetch('/.netlify/functions/instagram-conectar', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || 'No se pudo conectar Instagram')
  return data as T
}

/** Mismo tope de candidatas que el servidor (`MAX_CANDIDATAS` en `netlify/functions/utils/promo-instagram.ts`). */
const MAX_CANDIDATAS_MAILS = 30

const llamarPromo = async <T>(token: string, body: Record<string, unknown>): Promise<T> => {
  const res = await fetch('/.netlify/functions/instagram-promo', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || 'No se pudo completar la acción')
  return data as T
}

export const instagramService = {
  /** Cuenta activa más reciente de la organización, o null si todavía no conectó ninguna. */
  async obtenerCuenta(client: SupabaseClient<Database>, organizacionId: string): Promise<InstagramCuenta | null> {
    const { data, error } = await client
      .from('instagram_cuentas')
      .select(COLUMNAS)
      .eq('organizacion_id', organizacionId)
      .eq('activa', true)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
    if (error) throw error
    return data as InstagramCuenta | null
  },

  /** Último conteo de seguidores capturado de una cuenta, o null si todavía no hay métricas. */
  async ultimosSeguidores(client: SupabaseClient<Database>, organizacionId: string, cuentaId: string): Promise<number | null> {
    const { data, error } = await client
      .from('instagram_metricas_cuenta')
      .select('followers_count')
      .eq('organizacion_id', organizacionId)
      .eq('cuenta_id', cuentaId)
      .not('followers_count', 'is', null)
      .order('fecha', { ascending: false })
      .limit(1)
      .maybeSingle()
    if (error) throw error
    return data?.followers_count ?? null
  },

  /** Una fila por publicación del período, con su última foto y su foto a 7 días. */
  async rendimientoPublicaciones(
    client: SupabaseClient<Database>,
    organizacionId: string,
    periodo: Periodo,
  ): Promise<RendimientoPublicaciones> {
    const { data, error } = await client.rpc('reporte_instagram_publicaciones', {
      p_organizacion_id: organizacionId,
      p_desde: periodo.desde,
      p_hasta: periodo.hasta,
    })
    if (error) throw error
    const filas = data ?? []
    return { publicaciones: filas.map(aPublicacion), recortada: filas.length >= LIMITE_FILAS }
  },

  /** Métricas diarias de la cuenta activa entre dos fechas, ordenadas por fecha. */
  async serieCuenta(client: SupabaseClient<Database>, organizacionId: string, periodo: Periodo): Promise<FilaCuentaDia[]> {
    const { data, error } = await client
      .from('instagram_metricas_cuenta')
      .select('fecha, followers_count, follows, unfollows, instagram_cuentas!inner(activa)')
      .eq('organizacion_id', organizacionId)
      .eq('instagram_cuentas.activa', true)
      .gte('fecha', periodo.desde)
      .lte('fecha', periodo.hasta)
      .order('fecha')
    if (error) throw error
    return (data ?? []).map(({ fecha, followers_count, follows, unfollows }) => ({
      fecha,
      followers_count,
      follows,
      unfollows,
    }))
  },

  /**
   * Publicaciones que pueden aparecer en el bloque de los mails, con el mismo criterio y el mismo tope que
   * `promo_instagram_datos` en el servidor: portada copiada, últimos 90 días, vistas por la captura en los
   * últimos 3 días, con su tema y su máximo de vistas de los últimos 30 días. El tope de 30 se aplica solo a
   * las no ocultas, igual que en el servidor, así la vista previa elige entre las mismas que el mail (ORR-607).
   * Las ocultas vienen aparte, con su propio tope, para poder volver a mostrarlas.
   * Las columnas son de la migración 20261028000000: hasta regenerar database.ts va sin tipar.
   */
  async publicacionesParaMails(
    client: SupabaseClient<Database>,
    organizacionId: string,
    cuentaId: string,
    ahora = new Date(),
  ): Promise<PublicacionParaMail[]> {
    const dia = 86_400_000
    const db = client as unknown as SupabaseClient
    const candidatas = (ocultas: boolean) =>
      db
        .from('instagram_publicaciones')
        .select('id, permalink, caption, media_type, es_reel, imagen_url, publicado_at, ocultar_en_mails')
        .eq('organizacion_id', organizacionId)
        .eq('cuenta_id', cuentaId)
        .eq('ocultar_en_mails', ocultas)
        .not('en_perfil', 'is', false)
        .not('imagen_url', 'is', null)
        .gte('publicado_at', new Date(ahora.getTime() - 90 * dia).toISOString())
        .gte('vista_at', new Date(ahora.getTime() - 3 * dia).toISOString())
        .order('publicado_at', { ascending: false })
        .limit(MAX_CANDIDATAS_MAILS)
    const [visibles, ocultas] = await Promise.all([candidatas(false), candidatas(true)])
    if (visibles.error) throw visibles.error
    if (ocultas.error) throw ocultas.error
    type Fila = Omit<PublicacionParaMail, 'tema' | 'vistas'>
    const filas = [...((visibles.data ?? []) as Fila[]), ...((ocultas.data ?? []) as Fila[])].sort((a, b) =>
      b.publicado_at.localeCompare(a.publicado_at),
    )
    if (!filas.length) return []

    const ids = filas.map((f) => f.id)
    const [temas, metricas] = await Promise.all([
      db.from('instagram_publicacion_temas').select('publicacion_id, tema').eq('organizacion_id', organizacionId).in('publicacion_id', ids),
      db
        .from('instagram_metricas_publicacion')
        .select('publicacion_id, views')
        .eq('organizacion_id', organizacionId)
        .in('publicacion_id', ids)
        .gte('fecha', new Date(ahora.getTime() - 30 * dia).toISOString().slice(0, 10)),
    ])
    if (temas.error) throw temas.error
    if (metricas.error) throw metricas.error
    const temaDe = new Map(((temas.data ?? []) as { publicacion_id: string; tema: string }[]).map((t) => [t.publicacion_id, t.tema]))
    const vistasDe = new Map<string, number>()
    for (const m of (metricas.data ?? []) as { publicacion_id: string; views: number | null }[]) {
      if (typeof m.views === 'number') vistasDe.set(m.publicacion_id, Math.max(vistasDe.get(m.publicacion_id) ?? 0, m.views))
    }
    return filas.map((f) => ({ ...f, tema: temaDe.get(f.id) ?? null, vistas: vistasDe.get(f.id) ?? null }))
  },

  /**
   * true si el cliente pidió no ver el bloque de Instagram en sus mails (Ley 25.326, art. 27).
   * `sin_promo_mails` es de la migración 20261029000000: hasta regenerar database.ts va sin tipar.
   */
  async clienteSinPromoMails(client: SupabaseClient<Database>, organizacionId: string, clienteId: string): Promise<boolean> {
    const { data, error } = await (client as unknown as SupabaseClient)
      .from('clientes')
      .select('sin_promo_mails')
      .eq('id', clienteId)
      .eq('organizacion_id', organizacionId)
      .maybeSingle()
    if (error) throw error
    return (data as { sin_promo_mails?: boolean } | null)?.sin_promo_mails === true
  },

  /** Marca o desmarca el opt-out del cliente, con la fecha en que se marcó. */
  async guardarClienteSinPromoMails(
    client: SupabaseClient<Database>,
    organizacionId: string,
    clienteId: string,
    sinPromo: boolean,
  ): Promise<void> {
    const { data, error } = await (client as unknown as SupabaseClient)
      .from('clientes')
      .update({ sin_promo_mails: sinPromo, sin_promo_mails_fecha: sinPromo ? new Date().toISOString() : null })
      .eq('id', clienteId)
      .eq('organizacion_id', organizacionId)
      .select('id')
    if (error) throw error
    // Un UPDATE que no toca filas no da error: sin esta verificación, una baja de Ley 25.326 que no se
    // guardó se vería como guardada.
    if (!data?.length) throw new Error('No se pudo guardar la preferencia del cliente')
  },

  /**
   * Clics en los links del bloque de Instagram, por tipo de mail, entre dos días argentinos. Va por la
   * function `instagram-promo`, que valida verReportes del lado del servidor: `email_clics` está cerrada
   * a anon y la lectura no puede ser una RPC SECURITY DEFINER abierta, que aceptaría cualquier organización.
   */
  async clicsDesdeMails(token: string, organizacionId: string, periodo: Periodo): Promise<ClicsPorTipo[]> {
    const { filas } = await llamarPromo<{ filas: ClicsPorTipo[] }>(token, {
      accion: 'clics',
      organizacionId,
      desde: periodo.desde,
      hasta: periodo.hasta,
    })
    return filas
  },

  /**
   * Saca una publicación del bloque de los mails, o la vuelve a poner. Va por la function
   * `instagram-promo`, que valida editarConfiguracion del lado del servidor.
   */
  async ocultarEnMails(token: string, organizacionId: string, publicacionId: string, ocultar: boolean): Promise<void> {
    await llamarPromo(token, { accion: 'ocultar', organizacionId, publicacionId, ocultar })
  },

  /** Guarda el tema de una publicación, o lo borra si viene null. Repetirlo deja lo mismo. */
  async guardarTema(
    client: SupabaseClient<Database>,
    organizacionId: string,
    publicacionId: string,
    tema: string | null,
  ): Promise<void> {
    const tabla = client.from('instagram_publicacion_temas')
    const { error } =
      tema === null
        ? await tabla.delete().eq('publicacion_id', publicacionId).eq('organizacion_id', organizacionId)
        : await tabla.upsert(
            { publicacion_id: publicacionId, organizacion_id: organizacionId, tema },
            { onConflict: 'publicacion_id' },
          )
    if (error) throw error
  },

  async iniciar(token: string, organizacionId: string): Promise<{ url: string }> {
    return llamarFunction(token, { accion: 'iniciar', organizacionId })
  },

  async canjear(token: string, organizacionId: string, code: string, state: string): Promise<{ username: string }> {
    return llamarFunction(token, { accion: 'canjear', organizacionId, code, state })
  },
}
